/**
 * AnimatedNumber — displays a numeric value with a brief accent-colour flash
 * whenever the value changes. Respects `prefers-reduced-motion`.
 */
import { useEffect, useRef, useState } from 'react';

export interface AnimatedNumberProps {
  value: number;
  decimals?: number;
  className?: string;
  'aria-label'?: string;
  'data-testid'?: string;
}

export function AnimatedNumber({
  value,
  decimals = 1,
  className,
  'aria-label': ariaLabel,
  'data-testid': testId = 'animated-number',
}: AnimatedNumberProps) {
  const [display, setDisplay] = useState(value);
  const [flashing, setFlashing] = useState(false);
  const prevRef = useRef(value);

  useEffect(() => {
    if (value === prevRef.current) return;
    prevRef.current = value;
    setDisplay(value);

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduced) {
      setFlashing(true);
      const id = setTimeout(() => setFlashing(false), 500);
      return () => clearTimeout(id);
    }
  }, [value]);

  return (
    <span
      className={[
        'tabular-nums transition-colors duration-500',
        flashing ? 'text-[color:var(--accent)]' : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-live="polite"
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {display.toFixed(decimals)}
    </span>
  );
}
